import React, { useState, useEffect, useCallback } from 'react';
import { Search, Filter, ChevronLeft, ChevronRight, MoreVertical, UserX, Crown, Trash2, X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const PAGE_SIZE = 10;

function UserDetailModal({ user, onClose, onAction }) {
  if (!user) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-lg rounded-2xl p-6" style={{ background: '#0D1B2A', border: '1px solid rgba(0,229,255,0.2)' }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>User Profile</h3>
          <button onClick={onClose} style={{ color: '#64748B' }}><X size={18} /></button>
        </div>
        <div className="flex items-center gap-4 mb-5">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-xl" style={{ background: 'linear-gradient(135deg, #00E5FF, #7B2FBE)' }}>
            {(user?.full_name || user?.email || 'U')?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <p className="text-white font-semibold" style={{ fontFamily: 'DM Sans, sans-serif' }}>{user?.full_name || 'No name'}</p>
            <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{user?.email}</p>
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: user?.role === 'admin' ? 'rgba(123,47,190,0.2)' : 'rgba(0,229,255,0.1)', color: user?.role === 'admin' ? '#7B2FBE' : '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>{user?.role || 'consumer'}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-5">
          {[{ label: 'Joined', value: user?.created_at ? new Date(user?.created_at)?.toLocaleDateString() : 'N/A' },
            { label: 'Onboarding', value: user?.onboarding_completed ? 'Complete' : 'Pending' }]?.map(item => (
            <div key={item?.label} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,229,255,0.08)' }}>
              <p className="text-xs mb-1" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{item?.label}</p>
              <p className="text-sm font-medium text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{item?.value}</p>
            </div>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => onAction('suspend', user)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium" style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)', fontFamily: 'DM Sans, sans-serif' }}>
            <UserX size={13} /> Suspend
          </button>
          <button onClick={() => onAction('upgrade', user)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium" style={{ background: 'rgba(123,47,190,0.1)', color: '#7B2FBE', border: '1px solid rgba(123,47,190,0.2)', fontFamily: 'DM Sans, sans-serif' }}>
            <Crown size={13} /> Upgrade Premium
          </button>
          <button onClick={() => onAction('delete', user)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium" style={{ background: 'rgba(239,68,68,0.05)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.15)', fontFamily: 'DM Sans, sans-serif' }}>
            <Trash2 size={13} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionMsg, setActionMsg] = useState('');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase?.from('user_profiles')?.select('*', { count: 'exact' });
      if (search) query = query?.or(`full_name.ilike.%${search}%,email.ilike.%${search}%`);
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      const { data, count, error } = await query?.order('created_at', { ascending: false })?.range(from, to);
      if (!error) {
        setUsers(data || []);
        setTotal(count || 0);
      }
    } catch (err) {
      console.error('Load users error:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterPlan]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // Real-time subscription for user changes
  useEffect(() => {
    const channel = supabase?.channel('admin_users_realtime')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'user_profiles' }, () => {
        loadUsers();
      })
      ?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [loadUsers]);

  const handleAction = async (action, user) => {
    setSelectedUser(null);
    try {
      if (action === 'suspend') {
        await supabase?.from('user_profiles')?.update({ role: 'suspended' })?.eq('id', user?.id);
        setActionMsg(`User ${user?.full_name || user?.email} suspended.`);
      } else if (action === 'upgrade') {
        await supabase?.from('budgets')?.update({ plan: 'premium' })?.eq('user_id', user?.id);
        setActionMsg(`User upgraded to Premium.`);
      } else if (action === 'delete') {
        if (!window.confirm(`Delete user ${user?.email}? This cannot be undone.`)) return;
        await supabase?.from('user_profiles')?.delete()?.eq('id', user?.id);
        setActionMsg(`User deleted.`);
      }
      loadUsers();
      setTimeout(() => setActionMsg(''), 3000);
    } catch (err) {
      console.error('Action error:', err);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>User Management</h2>
        <span className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{total?.toLocaleString()} total users</span>
      </div>

      {actionMsg && (
        <div className="px-4 py-2.5 rounded-xl text-sm" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', color: '#10B981', fontFamily: 'DM Sans, sans-serif' }}>
          {actionMsg}
        </div>
      )}

      {/* Search & Filter */}
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#64748B' }} />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e?.target?.value); setPage(1); }}
            placeholder="Search by name or email..."
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
            style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', fontFamily: 'DM Sans, sans-serif' }}
          />
        </div>
        <select
          value={filterPlan}
          onChange={(e) => { setFilterPlan(e?.target?.value); setPage(1); }}
          className="px-3 py-2.5 rounded-xl text-sm outline-none"
          style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}
        >
          <option value="all">All Plans</option>
          <option value="free">Free</option>
          <option value="premium">Premium</option>
        </select>
      </div>

      {/* Table */}
      <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,229,255,0.1)' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ background: 'rgba(0,0,0,0.4)' }}>
                {['User', 'Email', 'Role', 'Onboarding', 'Joined', 'Actions']?.map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Loading users...</td></tr>
              ) : users?.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>No users found</td></tr>
              ) : users?.map((user, i) => (
                <tr key={user?.id} style={{ borderTop: '1px solid rgba(0,229,255,0.06)', background: i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: 'linear-gradient(135deg, #00E5FF33, #7B2FBE33)' }}>
                        {(user?.full_name || user?.email || 'U')?.charAt(0)?.toUpperCase()}
                      </div>
                      <span className="text-sm text-white truncate max-w-[120px]" style={{ fontFamily: 'DM Sans, sans-serif' }}>{user?.full_name || 'No name'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm truncate max-w-[160px]" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{user?.email}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-1 rounded-full" style={{ background: user?.role === 'admin' ? 'rgba(123,47,190,0.15)' : user?.role === 'suspended' ? 'rgba(239,68,68,0.1)' : 'rgba(0,229,255,0.08)', color: user?.role === 'admin' ? '#7B2FBE' : user?.role === 'suspended' ? '#EF4444' : '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>{user?.role || 'consumer'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-1 rounded-full" style={{ background: user?.onboarding_completed ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)', color: user?.onboarding_completed ? '#10B981' : '#F59E0B', fontFamily: 'DM Sans, sans-serif' }}>{user?.onboarding_completed ? 'Done' : 'Pending'}</span>
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{user?.created_at ? new Date(user?.created_at)?.toLocaleDateString() : 'N/A'}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => setSelectedUser(user)} className="p-1.5 rounded-lg transition-colors" style={{ color: '#64748B' }}>
                      <MoreVertical size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', color: page === 1 ? '#334155' : '#00E5FF' }}>
              <ChevronLeft size={14} />
            </button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-lg" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', color: page === totalPages ? '#334155' : '#00E5FF' }}>
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {selectedUser && <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} onAction={handleAction} />}
    </div>
  );
}
